import { useCallback, useEffect } from 'react';

import { logger } from 'utils/logger';

/**
 * It is used for lazy loading components
 * @example
 * useInfiniteScroll({ ref: useRef(), loadMore: () => {}, isLoading: false, isLoadMorePagesAvailable: false })
 * @param {object} ref
 * @param {function} loadMore
 * @param {boolean} isLoading
 * @param {boolean} isLoadMorePagesAvailable
 * @returns {undefined}
 */
const useInfiniteScroll = ({ ref, loadMore, isLoading, isLoadMorePagesAvailable }) => {
  const scrollListener = useCallback(() => {
    const currentElem = ref?.current;
    const bottomPositionPx = currentElem?.scrollHeight - currentElem?.clientHeight;
    const thresholdPx = 350;

    const isScrollHeightEnough = currentElem?.scrollTop > bottomPositionPx - thresholdPx;

    if (isScrollHeightEnough && isLoadMorePagesAvailable && !isLoading) {
      loadMore();
    }
  }, [ref, isLoadMorePagesAvailable, loadMore, isLoading]);

  useEffect(() => {
    const currentElem = ref?.current;
    currentElem?.addEventListener('scroll', scrollListener);

    return () => currentElem?.removeEventListener('scroll', scrollListener);
  }, [ref, scrollListener]);

  if (!ref) {
    return logger.error('ref is required for useInfiniteScroll util');
  }
};

export default useInfiniteScroll;
